/**
 * Гибкий `className`: строка (один класс для корня) или объект с классами по ключам (`root`, `header`, `content` и т.д.). \
 * Удобно для API компонентов, где одна пропа может задавать класс точечно для разных частей.
 *
 * @template K имена ключей частей компонента (например `'root' | 'content'`)
 *
 * @example
 * // Строка — один класс, обычно применяется к корневому элементу
 * className="my-section"
 *
 * @example
 * // Объект — классы по ключам для разных частей компонента
 * className={{ root: "form-section", content: "form-section__body" }}
 *
 * @example
 * // В Form.Section: root — обёртка секции, content — внутренний div
 * <Form.Section className={{ root: "bordered", content: "padded" }}>...</Form.Section>
 *
 * @example
 * // В Table column meta: header — ячейка заголовка, headerContent — контейнер заголовка и иконки
 * meta={{ headerClassName: { header: "compact", headerContent: "center" } }}
 */
export type ClassNameMap<K extends string = string> = string | Partial<Record<K, string>>;

/**
 * Приводит значение `className` к объекту классов по ключам. \
 * Строка трактуется как класс для корня (`rootKey`); объект возвращается как есть (с доп. ключами по необходимости).
 *
 * @template K имена ключей (то же множество, что у `ClassNameMap<K>`)
 * @param value строка, объект по ключам или `undefined`
 * @param rootKey ключ, в который подставляется значение, если передан `string`
 * @returns объект с классами по ключам (отсутствующие ключи — `undefined`)
 *
 * @example
 * parseClassName('myRoot', 'root') // { root: 'myRoot' }
 * parseClassName({ root: 'a', content: 'b' }, 'root') // { root: 'a', content: 'b' }
 */
export function parseClassName<K extends string>(
    value: ClassNameMap<K> | undefined,
    rootKey: K,
): Partial<Record<K, string>> {
    if (value == null) return {};
    if (typeof value === "string") return { [rootKey]: value } as Partial<Record<K, string>>;
    return { ...value };
}
